import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useEffect } from "react";
import { Grid } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { AuthStore } from "stores/AuthStore";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import AcxVirtualizedSelectMulti from "components/UI/Select/AcxVirtualizedSelectMulti";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import { useRestrictedOptions } from "../BaseFilterComponents/useRestrictedOptions";

const DEFAULT_ALLOWED_MODES = ["include", "exclude"] as const;

export const PrimaryDriversFilter = observer(function (
    props: IApplicationFilterComponent,
) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    const { availableOptions: availablePrimaryDrivers } = useRestrictedOptions({
        options: valuesStore.primaryDrivers,
        getOptionValue: (driver) => driver.driverIndex,
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });
    const allowedModes =
        props.modeRestriction?.allowedModes ?? DEFAULT_ALLOWED_MODES;
    const modeOptions: Array<{
        label: string;
        value: boolean;
        isDisabled?: boolean;
    }> = [
        {
            label: "is one of",
            value: true,
            isDisabled: !allowedModes.includes("include"),
        },
        {
            label: "is not one of",
            value: false,
            isDisabled: !allowedModes.includes("exclude"),
        },
    ];

    useEffect(() => {
        if (!props.modeRestriction) return;
        const currentMode = props.store.containsDrivers
            ? "include"
            : "exclude";
        if (allowedModes.includes(currentMode)) return;
        props.store.setContainsDrivers(allowedModes[0] === "include");
    }, [allowedModes, props.modeRestriction, props.store]);

    if (!authStore.canUserView("Drivers")) return null;

    return (
        <FilterSectionContainer>
            <Grid id={props.id} container item direction="column">
                <Grid item>
                    <AcxSelectSingle
                        id={"application-filters-primary-drivers-contains"}
                        options={modeOptions}
                        labelField="label"
                        valueField="value"
                        defaultValue={{
                            label: props.store.containsDriversString,
                            value: props.store.containsDrivers,
                        }}
                        customStyle={customFilterSelectStyles}
                        onChange={(item) => {
                            if (item.isDisabled) return;
                            props.store.setContainsDrivers(item.value);
                        }}
                        isDisabled={
                            modeOptions.filter((item) => !item.isDisabled)
                                .length <= 1
                        }
                    />
                </Grid>
                <AcxVirtualizedSelectMulti
                    id={"drivers-filter-selector"}
                    defaultSelectedIds={props.store.drivers.map((driver) =>
                        driver.driverIndex.toString(),
                    )}
                    onApply={(selectedIds: string[]) => {
                        if (!valuesStore.primaryDrivers) return;
                        props.store.setDrivers(
                            availablePrimaryDrivers.filter((driver) =>
                                selectedIds.includes(
                                    driver.driverIndex.toString(),
                                ),
                            ),
                        );
                    }}
                    options={availablePrimaryDrivers}
                    isLoading={valuesStore.getTaskLoading(
                        ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_DRIVERS,
                    )}
                    itemName="Driver"
                    labelField="driverName"
                    idField="driverIndex"
                    menuMinWidth={360}
                    wrapOptionLabels
                    maxSelectionCount={10000}
                    warningMessage={
                        "No more than 10k drivers may be selected at once."
                    }
                />
            </Grid>
        </FilterSectionContainer>
    );
});
